import ConversionTracker from "./services/ConversionTracker";
import EventTracker from "./services/EventTracker";
import UserData from "./services/UserData";
import SentryHelper from "./services/SentryHelper";
import OptimizelyExperiment from "./services/OptimizelySXExperiment";

import store from "@utils/redux/store";
import { updateRecommendedPlan } from "@utils/redux/slices/recommendedPlan";
import { updatePlans } from "@utils/redux/slices/plans";
import { updateServerContext } from "@utils/redux/slices/serverContext";
import {
  updateUserData,
  initializeNewUser,
  fetchAndInitializeExistingUser,
} from "@utils/redux/slices/userData";
import { updateTrialFromServerContext } from "@utils/redux/slices/trial";
import { setLanguage } from "@utils/redux/slices/language";
import { setCountry } from "@utils/redux/slices/countryCode";
import { getEmailAndName } from "@utils/urlParams";

import i18n from "@i18n";
import VisitTracker from "./services/VisitTracker";
import ExperimentTracker from "./services/ExperimentTracker";
import getContext from "./context";
import { USER_IS_BOT } from "./botDetector";

/**
 * Initialize global services and variables for use within our components.
 */
export default function initializeGlobalServices(
  serverContext,
  userIsBot = false
) {
  // Set up Sentry and set the user.
  window.SentryHelper = new SentryHelper(serverContext.user_id);
  window.serverContext = serverContext;
  window.trialFeeWaived = serverContext.trial_fee_waived;

  // Update i18n language to match serverContext language.
  if (i18n && serverContext.language_code !== i18n.language) {
    i18n.changeLanguage(serverContext.language_code);
  }

  if ("plans" in serverContext && 4 in serverContext.plans) {
    window.trialFee = serverContext.plans[4].trial_fee;
  } else if ("plan" in serverContext) {
    window.trialFee = serverContext.plan.trial_fee;
  }
  window.hasTrialFee = !window.trialFeeWaived && window.trialFee > 0;

  window.experimentTracker = new ExperimentTracker();
  window.visitTracker = new VisitTracker(serverContext.session_cookie_domain);

  // Set up the event trackers and make them available globally.
  window.tracker = new EventTracker(userIsBot);

  // Set up the userData stub
  window.userData = new UserData();

  // Set up conversion tracker for pixel tracking.
  window.conversionTracker = new ConversionTracker();

  window.osxe = new OptimizelyExperiment();

  document.querySelector("html").lang = serverContext.language_code;
}

/**
 * Get serverContext on initialization from cache with retry if fetching from cache fails.
 */
export async function getServerContextOnInitialization() {
  try {
    return await window.serverContextLoaded;
  } catch (err) {
    console.error(err);
    window.serverContextLoaded = getContext();
    return window.serverContextLoaded;
  }
}

/**
 * Initialize both global services taking into account if the user is a bot.
 * @param {Object} serverContext
 */
export function initializeGlobalServicesWithBotDetection(serverContext) {
  // NOTE(patrick): We had issues with bots with duplicated cookies or application
  // state getting lumped into one experiment variation which would mess up
  // experiment reporting. We use a bot detection library and make sure they get
  // a minimal experience.
  initializeGlobalServices(serverContext, USER_IS_BOT);
}

/**
 * After we fetch serverContext, Take the data and update the appropriate
 * redux slices with it.
 * @param {*} serverContext
 */
export async function updateStoreWithServerContext(serverContext) {
  store.dispatch(updateServerContext(serverContext));
  store.dispatch(setLanguage(serverContext.language_code));
  store.dispatch(setCountry(serverContext.country_code));
  store.dispatch(updateTrialFromServerContext(serverContext));

  if (serverContext?.plans) store.dispatch(updatePlans(serverContext.plans));
  if (serverContext?.plan)
    store.dispatch(updateRecommendedPlan(serverContext.plan));

  // Guard against bots, don't attempt to fetch userData if we detect a bot.
  if (USER_IS_BOT) return;
  // Fetch user from backend if they are in the database.
  if (serverContext.user_in_db) {
    store.dispatch(
      fetchAndInitializeExistingUser(serverContext.user_id, serverContext)
    );
  } else {
    store.dispatch(initializeNewUser(serverContext));
  }
  const emailAndName = getEmailAndName();
  if (Object.keys(emailAndName).length !== 0)
    await store.dispatch(updateUserData(emailAndName));
}
